import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { useQuery, useQueryClient } from 'react-query'
import { useAuth } from './useAuth'
import api from '../services/api'
import {
  clearSelectedClientId,
  getSelectedClientId,
  setSelectedClientId as storeSetSelectedClientId,
  subscribeSelectedClientId,
} from '../services/clientFilterStore'

const ClientFilterContext = createContext(null)

function clientLabel(c) {
  if (!c) return ''
  const name = c.business_name || c.nombre_comercial_pdf || [c.nombre, c.apellido_paterno].filter(Boolean).join(' ')
  return name ? `${name} (${c.rfc})` : c.rfc || `Cliente #${c.id}`
}

export const ClientFilterProvider = ({ children }) => {
  const { isAuthenticated, user } = useAuth()
  const queryClient = useQueryClient()
  const [selectedClientId, setSelectedState] = useState(() => getSelectedClientId())

  useEffect(() => subscribeSelectedClientId(setSelectedState), [])

  // Si logout / sin auth → limpiar
  useEffect(() => {
    if (!isAuthenticated) {
      clearSelectedClientId()
    }
  }, [isAuthenticated])

  const { data: clients = [], isLoading } = useQuery(
    ['global-filter-clients', user?.id],
    async () => {
      const response = await api.get('/clients/clients/', {
        params: { estado: 'activo', page_size: 1000 },
      })
      return response.data.results || response.data || []
    },
    { enabled: !!isAuthenticated && !!user }
  )

  // Si el id guardado ya no es accesible, resetear
  useEffect(() => {
    if (!isAuthenticated || isLoading) return
    if (selectedClientId == null) return
    const ok = clients.some((c) => c.id === selectedClientId)
    if (!ok && clients.length > 0) {
      clearSelectedClientId()
    }
  }, [clients, selectedClientId, isAuthenticated, isLoading])

  const setSelectedClientId = useCallback(
    (id) => {
      storeSetSelectedClientId(id)
      if (queryClient) {
        queryClient.invalidateQueries()
      }
    },
    [queryClient]
  )

  const selectedClient = useMemo(
    () => clients.find((c) => c.id === selectedClientId) || null,
    [clients, selectedClientId]
  )

  const value = useMemo(
    () => ({
      selectedClientId,
      selectedClient,
      setSelectedClientId,
      clients,
      isLoadingClients: isLoading,
      clientLabel,
      isFiltering: selectedClientId != null,
    }),
    [selectedClientId, selectedClient, setSelectedClientId, clients, isLoading]
  )

  return (
    <ClientFilterContext.Provider value={value}>
      {children}
    </ClientFilterContext.Provider>
  )
}

export const useClientFilter = () => {
  const ctx = useContext(ClientFilterContext)
  if (!ctx) {
    throw new Error('useClientFilter debe usarse dentro de ClientFilterProvider')
  }
  return ctx
}

export default ClientFilterContext
